/**
 * socialApiShared.jsx — piezas comunes de los tableros orgánicos por API oficial.
 *
 * Instagram, Facebook y TikTok viven como pestañas de una sola pantalla
 * (pages/SocialOrganicPage) pero cada red tiene sus métricas. Lo que comparten
 * vive aquí: formateo según idioma, rango de fechas, tarjeta de KPI y estados.
 * BeneficiosPage también usa numFmt / Kpi / ErrorState / LoadingState.
 *
 * Estilos: components/SocialApi/socialApi.css (la importa cada página).
 */
import React, { useEffect, useRef, useState } from 'react';
import { FaExclamationTriangle, FaSync, FaSpinner, FaInfoCircle } from 'react-icons/fa';

// ── Formateo según el idioma de la app ───────────────────────────────────────
// El idioma lo fija la pantalla que pinta (setSocialLocale) antes de renderizar
// a sus hijos. Es un valor de módulo porque los builders de gráficas son
// funciones planas fuera de React; es-MX es el valor por defecto de siempre.
const LOCALES = { en: 'en-US', es: 'es-MX', pt: 'pt-BR' };
let currentLocale = 'es-MX';
const fmtCache = new Map();
const intl = (key, opts) => {
  const k = `${currentLocale}|${key}`;
  if (!fmtCache.has(k)) fmtCache.set(k, new Intl.NumberFormat(currentLocale, opts));
  return fmtCache.get(k);
};

export function setSocialLocale(language) {
  currentLocale = LOCALES[language] || 'es-MX';
}
export const getSocialLocale = () => currentLocale;

export const numFmt = { format: (n) => intl('num').format(n) };
export const compact = (n) => (n == null ? '—' : intl('compact', { notation: 'compact', maximumFractionDigits: 1 }).format(n));
export const pct2 = (n) => (n == null ? '—' : `${(n * 100).toFixed(2)}%`);
export const pct1 = (n) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);

export const shortDate = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(currentLocale, { day: '2-digit', month: 'short' });
};
/** Fecha legible para humanos: "24 sep 2026" / "Sep 24, 2026". Acepta YYYY-MM-DD o ISO. */
export const longDate = (ts) => {
  if (!ts) return '';
  const s = String(ts);
  // YYYY-MM-DD a secas se interpreta en UTC y en México se corre un día atrás.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T12:00:00`) : new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(currentLocale, { day: 'numeric', month: 'short', year: 'numeric' });
};
export const shortText = (c, n = 26) => {
  const s = (c || '').trim();
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

// ── Rango de fechas ──────────────────────────────────────────────────────────
export const PRESET_DAYS = { '7d': 7, '30d': 30, '90d': 90 };

export const ymd = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

export function resolveRange(range) {
  if (!range || range.preset === 'all') return { from: null, to: null };
  if (range.preset === 'custom') return { from: range.from || null, to: range.to || null };
  const days = PRESET_DAYS[range.preset];
  if (!days) return { from: null, to: null };
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: ymd(from), to: ymd(to) };
}

/** Filtra por `timestamp` (ISO). Vale para posts de IG, Página y videos de TikTok. */
export function filterByRange(items, range) {
  const { from, to } = resolveRange(range);
  if (!from && !to) return items;
  return items.filter((p) => {
    const d = (p.timestamp || '').slice(0, 10);
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
}

/**
 * Etiqueta HONESTA de la ventana de los KPIs de insights. Cada red acota estas
 * métricas a un máximo de días (IG ~30, Páginas ~90, TikTok 30) terminando al
 * final del rango: con rangos cortos el número cubre exactamente el periodo
 * pedido; con rangos largos, solo la ventana máxima, y así se dice.
 */
export function insightWindowLabel(range, maxDays, t) {
  if (range.preset === 'custom') {
    if (range.from && range.to) {
      const days = Math.round((new Date(range.to) - new Date(range.from)) / 86400000);
      return days > 0 && days <= maxDays
        ? t('organic.periodRange', { from: longDate(range.from), to: longDate(range.to) })
        : t('organic.periodCapped', { n: maxDays });
    }
    return t('organic.periodCapped', { n: maxDays });
  }
  const days = PRESET_DAYS[range.preset];
  return days && days <= maxDays
    ? t('organic.periodLast', { n: days })
    : t('organic.periodCapped', { n: maxDays });
}

// ── Tarjeta de KPI ───────────────────────────────────────────────────────────
// `hint` es la explicación en lenguaje llano que aparece al pasar por el ⓘ:
// el usuario de la plataforma no es técnico y "alcance" vs "visualizaciones"
// no es obvio.
export function Kpi({ icon, accent, label, value, sub, hint }) {
  return (
    <div className="soc-kpi" style={{ '--soc-accent': accent }}>
      <div className="soc-kpi-head">
        <span className="soc-kpi-label">
          {label}
          <Hint text={hint} />
        </span>
        <span className="soc-kpi-icon">{icon}</span>
      </div>
      <div className="soc-kpi-value">{value}</div>
      {sub && <div className="soc-kpi-sub">{sub}</div>}
    </div>
  );
}

/**
 * ⓘ con explicación. Se abre al pasar el mouse Y al tocarlo (en celular no hay
 * hover), y se cierra tocando fuera. La burbuja va en position:fixed y se acota
 * a la pantalla: anclada al ícono, en la columna derecha de un celular se salía
 * por el borde y quedaba cortada.
 */
export function Hint({ text }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);   // null = cerrada

  const open = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth;
    const width = Math.min(240, vw - 24);
    const left = Math.max(12, Math.min(r.left + r.width / 2 - width / 2, vw - width - 12));
    // Arriba del ícono si hay espacio; si no (cerca del borde superior), abajo.
    const above = r.top > 110;
    setPos({ left, width, top: above ? r.top - 8 : r.bottom + 8, above });
  };
  const close = () => setPos(null);

  useEffect(() => {
    if (!pos) return undefined;
    const onAway = (e) => { if (!ref.current?.contains(e.target)) close(); };
    // Al hacer scroll la burbuja fija se despegaría del ícono: se cierra.
    const onScroll = () => close();
    document.addEventListener('pointerdown', onAway);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onAway);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [pos]);

  if (!text) return null;
  return (
    <span
      ref={ref}
      className="soc-hint"
      role="button"
      tabIndex={0}
      aria-label={text}
      aria-expanded={!!pos}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      onClick={(e) => { e.stopPropagation(); if (pos) close(); else open(); }}
    >
      <FaInfoCircle />
      {pos && (
        <span
          className={`soc-hint-bubble ${pos.above ? 'above' : 'below'}`}
          role="tooltip"
          style={{ left: pos.left, top: pos.top, width: pos.width }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

// ── Estados de página ────────────────────────────────────────────────────────
// Textos por defecto en español: BeneficiosPage los usa sin pasarlos.
export function ErrorState({ message, onRetry, title, retryLabel }) {
  return (
    <main className="soc-page">
      <div className="soc-state">
        <FaExclamationTriangle className="big" />
        <h3>{title || 'No se pudieron cargar las métricas'}</h3>
        <p>{message}</p>
        <button className="soc-btn" onClick={onRetry}><FaSync /> {retryLabel || 'Reintentar'}</button>
      </div>
    </main>
  );
}

export function LoadingState({ label }) {
  return <div className="soc-state"><FaSpinner className="soc-spin big" /><p>{label}</p></div>;
}

// ── Árbol de comentarios (mismo shape en IG y Facebook) ──────────────────────
export function buildCommentTree(comments) {
  const byId = new Map();
  comments.forEach((c) => byId.set(c.id, { ...c, children: [] }));
  const roots = [];
  comments.forEach((c) => {
    const node = byId.get(c.id);
    if (c.parent_id && byId.has(c.parent_id)) byId.get(c.parent_id).children.push(node);
    else roots.push(node);
  });
  return roots;
}
